import { Search, Calendar, Loader2, Check } from "lucide-react";
import { formatPrice, formatDate, isOnRequestBooking } from "@ckgo2/shared/utils";
import type { Trip, TripDate } from "./types";

interface TripStepProps {
  tripSearch: string;
  setTripSearch: (value: string) => void;
  tripsLoading: boolean;
  filteredTrips: Trip[];
  selectedTrip: Trip | null;
  setSelectedTrip: (trip: Trip) => void;
  selectedDate: TripDate | null;
  setSelectedDate: (date: TripDate | null) => void;
  requestedDate: string;
  setRequestedDate: (date: string) => void;
  tripDetailsLoading: boolean;
  tripDates: TripDate[];
  canProceedToCustomer: boolean;
  onNext: () => void;
}

export default function TripStep({
  tripSearch,
  setTripSearch,
  tripsLoading,
  filteredTrips,
  selectedTrip,
  setSelectedTrip,
  selectedDate,
  setSelectedDate,
  requestedDate,
  setRequestedDate,
  tripDetailsLoading,
  tripDates,
  canProceedToCustomer,
  onNext,
}: TripStepProps) {
  // Booking axis: on-request picks a day on the calendar; fixed-departures uses the dropdown.
  const isOnRequest = isOnRequestBooking(selectedTrip ?? {});
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select trip and date</h2>

      {/* Trip search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search trips..."
          value={tripSearch}
          onChange={(e) => setTripSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Trips list */}
      {tripsLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {filteredTrips.map((trip) => (
            <button
              key={trip.id}
              onClick={() => {
                setSelectedTrip(trip);
                setSelectedDate(null);
                setRequestedDate("");
              }}
              className={`flex w-full items-center gap-4 rounded-lg border p-3 text-left transition-colors ${
                selectedTrip?.id === trip.id
                  ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                  : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
              }`}
            >
              {trip.featuredImage && (
                <img
                  src={trip.featuredImage}
                  alt={trip.title}
                  className="h-12 w-16 rounded object-cover"
                />
              )}
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">{trip.title}</p>
                <p className="text-sm text-gray-500">{trip.durationDays} days</p>
              </div>
              {selectedTrip?.id === trip.id && <Check className="text-primary-600 h-5 w-5" />}
            </button>
          ))}
        </div>
      )}

      {/* Date selection: dropdown for fixed departures, date picker for on-request */}
      {selectedTrip && (
        <div className="space-y-3 border-t pt-4 dark:border-gray-700">
          {isOnRequest ? (
            <>
              <h3 className="font-medium text-gray-900 dark:text-white">Requested date</h3>
              <input
                type="date"
                value={requestedDate}
                onChange={(e) => setRequestedDate(e.target.value)}
                className="input"
                min={new Date().toISOString().slice(0, 10)}
              />
              <p className="text-xs text-gray-500">
                Customer can pick any day for this experience.
              </p>
            </>
          ) : (
            <>
              <h3 className="font-medium text-gray-900 dark:text-white">Available dates</h3>
              {tripDetailsLoading ? (
                <div className="flex h-16 items-center justify-center">
                  <Loader2 className="text-primary-600 h-5 w-5 animate-spin" />
                </div>
              ) : tripDates.length > 0 ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {tripDates
                    .filter((d) => d.status !== "cancelled")
                    .map((date) => (
                      <button
                        key={date.id}
                        onClick={() => setSelectedDate(date)}
                        className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                          selectedDate?.id === date.id
                            ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                            : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900 dark:text-white">
                            {formatDate(date.startDate)}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-primary-600 font-medium">{formatPrice(date.price)}</p>
                          <p className="text-xs text-gray-500">
                            {date.capacity - date.bookedCount} spots
                          </p>
                        </div>
                      </button>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500">No active dates</p>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <button onClick={onNext} disabled={!canProceedToCustomer} className="btn-primary">
          Continue
        </button>
      </div>
    </div>
  );
}
